import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { processError, type ProcessedError } from '@/lib/error-utils';

/**
 * Hook for centralized error handling in React components
 * 
 * @example
 * const { handleError, getErrorInfo } = useErrorHandler();
 * 
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   handleError(error); // Automatically shows toast
 * }
 */
export function useErrorHandler() {
  /**
   * Handle error and display toast notification
   */
  const handleError = useCallback((error: unknown, customMessage?: string) => {
    const processedError = processError(error);
    
    // Use react-hot-toast instead of sonner
    toast.error(`${processedError.title}: ${customMessage || processedError.message}`);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error handled:', error);
    }

    return processedError;
  }, []);

  /**
   * Get processed error information without displaying toast
   */
  const getErrorInfo = useCallback((error: unknown): ProcessedError => {
    return processError(error);
  }, []);

  /**
   * Handle error with custom callback
   */
  const handleErrorWithCallback = useCallback(
    (error: unknown, callback: (processedError: ProcessedError) => void) => {
      const processedError = processError(error);
      callback(processedError);
      return processedError;
    },
    []
  );

  return {
    handleError,
    getErrorInfo,
    handleErrorWithCallback,
  };
}
